//src/services/doctorApi.js
import API from "./interceptor";

// ******************* Create new doctor *******************
export const createDoctorDetails = async (doctorData) => {
  try {
    const response = await API.post(`/api/doctors`, doctorData);
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to create doctor profile"
    );
  }
};

// ******************* Get all doctors with filtering and pagination *******************
export const getAllDoctors = async (queryParams = {}) => {
  try {
    const response = await API.get(`/api/doctors`, { params: queryParams });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch doctors"
    );
  }
};

// ******************* Get single doctor by ID *******************
export const getDoctorById = async (doctorId) => {
  try {
    const response = await API.get(`/api/doctors/${doctorId}`);
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch doctor"
    );
  }
};

// ******************* Update doctor information *******************
export const updateDoctor = async (doctorId, updateData) => {
  try {
    const response = await API.put(`/api/doctors/${doctorId}`, updateData);
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to update doctor"
    );
  }
};

// ******************* Delete doctor *******************
export const deleteDoctor = async (doctorId) => {
  try {
    const response = await API.delete(`/api/doctors/${doctorId}`);
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to delete doctor"
    );
  }
};

// ******************* Additional utility functions *******************

// Get doctors by specialization
export const getDoctorsBySpecialization = async (specialization) => {
  try {
    const response = await API.get(`/api/doctors`, { 
      params: { specialization } 
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch doctors by specialization"
    );
  }
};

// Get available doctors
export const getAvailableDoctors = async () => {
  try {
    const response = await API.get(`/api/doctors`, { 
      params: { availability: 'available' } 
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch available doctors"
    );
  }
};

// Get doctors by department
export const getDoctorsByDepartment = async (department) => {
  try {
    const response = await API.get(`/api/doctors`, { 
      params: { department } 
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch doctors by department"
    );
  }
};

// Get doctors by experience level
export const getDoctorsByExperience = async (minExperience) => {
  try {
    const response = await API.get(`/api/doctors`, { 
      params: { experience: minExperience } 
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch doctors by experience"
    );
  }
};

// Get doctors by rating
export const getDoctorsByRating = async (minRating) => {
  try {
    const response = await API.get(`/api/doctors`, { 
      params: { rating: minRating } 
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch doctors by rating"
    );
  }
};

// Search doctors with multiple filters
export const searchDoctors = async (filters) => {
  try {
    const response = await API.get(`/api/doctors`, { params: filters });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to search doctors"
    );
  }
};

// Get doctors by location/clinic
export const getDoctorsByLocation = async (location) => {
  try {
    const response = await API.get(`/api/doctors`, { 
      params: { location } 
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch doctors by location"
    );
  }
};

// Get active doctors (status: active)
export const getActiveDoctors = async () => {
  try {
    const response = await API.get(`/api/doctors`, { 
      params: { status: 'active' } 
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch active doctors"
    );
  }
};

// Get inactive doctors (status: inactive)
export const getInactiveDoctors = async () => {
  try {
    const response = await API.get(`/api/doctors`, { 
      params: { status: 'inactive' } 
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch inactive doctors"
    );
  }
};

// Update doctor availability
export const updateDoctorAvailability = async (doctorId, availability) => {
  try {
    const response = await API.put(`/api/doctors/${doctorId}`, {
      availability
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to update doctor availability"
    );
  }
};

// Update doctor status
export const updateDoctorStatus = async (doctorId, status) => {
  try {
    const response = await API.put(`/api/doctors/${doctorId}`, {
      status
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to update doctor status"
    );
  }
};

// Activate doctor
export const activateDoctor = async (doctorId) => {
  try {
    const response = await API.put(`/api/doctors/${doctorId}`, {
      status: 'active'
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to activate doctor"
    );
  }
};

// Deactivate doctor
export const deactivateDoctor = async (doctorId) => {
  try {
    const response = await API.put(`/api/doctors/${doctorId}`, {
      status: 'inactive'
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to deactivate doctor"
    );
  }
};

// Get doctor's schedule/working hours
export const getDoctorSchedule = async (doctorId) => {
  try {
    const response = await API.get(`/api/doctors/${doctorId}/schedule`);
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch doctor schedule"
    );
  }
};

// Update doctor's schedule/working hours
export const updateDoctorSchedule = async (doctorId, scheduleData) => {
  try {
    const response = await API.put(`/api/doctors/${doctorId}/schedule`, scheduleData);
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to update doctor schedule"
    );
  }
};

// Get doctor's appointments
export const getDoctorAppointments = async (doctorId, queryParams = {}) => {
  try {
    const response = await API.get(`/api/doctors/${doctorId}/appointments`, { 
      params: queryParams 
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch doctor appointments"
    );
  }
};

// Get doctor statistics
export const getDoctorStats = async (doctorId) => {
  try {
    const response = await API.get(`/api/doctors/${doctorId}/stats`);
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch doctor statistics"
    );
  }
};


// ******************* Get doctor by email (URL parameter) *******************
export const getDoctorByEmail = async (email) => {
  try {
    const response = await API.get(`/api/doctors/email/${encodeURIComponent(email)}`);
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch doctor by email"
    );
  }
};

// ******************* Get doctor by email (Query parameter) *******************
export const getDoctorByEmailQuery = async (email) => {
  try {
    const response = await API.get(`/api/doctors/email-query`, {
      params: { email }
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch doctor by email"
    );
  }
};

// ******************* Get doctor by email with user details *******************
export const getDoctorByEmailWithUser = async (email) => {
  try {
    const response = await API.get(`/api/doctors/email-with-user/${encodeURIComponent(email)}`);
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to fetch doctor with user details"
    );
  }
};

// ******************* Search doctors by email pattern *******************
export const searchDoctorsByEmail = async (emailPattern) => {
  try {
    const response = await API.get(`/api/doctors/search/email`, {
      params: { email: emailPattern }
    });
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to search doctors by email"
    );
  }
};

// ******************* Check if doctor email exists *******************
export const checkDoctorEmailExists = async (email) => {
  try {
    const response = await API.get(`/api/doctors/check-email/${encodeURIComponent(email)}`);
    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || "Failed to check doctor email"
    );
  }
};

// ******************* Utility function to validate email format *******************
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// ******************* Get doctor profile by current user email *******************
export const getCurrentDoctorProfile = async () => {
  try {
    // Assuming you have a way to get current user's email
    const userEmail = localStorage.getItem('userEmail') || 
                     sessionStorage.getItem('userEmail');
    
    if (!userEmail) {
      throw new Error('No user email found');
    }

    const response = await getDoctorByEmail(userEmail);
    return response;
  } catch (error) {
    throw (
      error?.message || "Failed to fetch current doctor profile"
    );
  }
};

// ******************* Advanced email-based doctor search *******************
export const advancedDoctorEmailSearch = async (searchOptions) => {
  try {
    const {
      emailPattern,
      specialization,
      experience,
      limit = 10
    } = searchOptions;

    // First search by email pattern
    const emailResults = await searchDoctorsByEmail(emailPattern);
    
    let doctors = emailResults.data || [];

    // Apply additional filters if provided
    if (specialization) {
      doctors = doctors.filter(doctor => 
        doctor.specialization && 
        doctor.specialization.includes(specialization)
      );
    }

    if (experience) {
      doctors = doctors.filter(doctor => 
        doctor.experience >= experience
      );
    }

    // Limit results
    doctors = doctors.slice(0, limit);

    return {
      success: true,
      data: doctors,
      count: doctors.length
    };
  } catch (error) {
    throw (
      error?.message || "Failed to perform advanced email search"
    );
  }
};

// ******************* Batch email validation *******************
export const validateDoctorEmails = async (emails) => {
  try {
    const validationResults = await Promise.allSettled(
      emails.map(email => checkDoctorEmailExists(email))
    );

    const results = validationResults.map((result, index) => ({
      email: emails[index],
      valid: result.status === 'fulfilled' && result.value.exists,
      error: result.status === 'rejected' ? result.reason : null
    }));

    return {
      success: true,
      results: results
    };
  } catch (error) {
    throw (
      error?.message || "Failed to validate doctor emails"
    );
  }
};

// ******************* Get doctor suggestions by email domain *******************
export const getDoctorSuggestionsByDomain = async (domain) => {
  try {
    // Search for doctors with emails containing the domain
    const response = await searchDoctorsByEmail(`@${domain}`);
    
    return {
      success: true,
      data: response.data || [],
      count: response.count || 0
    };
  } catch (error) {
    throw (
      error?.message || "Failed to get doctor suggestions by domain"
    );
  }
};

// ******************* Update doctor email *******************
export const updateDoctorEmail = async (doctorId, newEmail) => {
  try {
    // First check if the new email already exists
    const emailExists = await checkDoctorEmailExists(newEmail);
    
    if (emailExists.exists) {
      throw new Error('Email already exists for another doctor');
    }

    // Update the doctor's email
    const response = await updateDoctor(doctorId, {
      contact: {
        email: newEmail
      }
    });

    return response;
  } catch (error) {
    throw (
      error?.message || "Failed to update doctor email"
    );
  }
};

// ******************* Get doctor by email with error handling *******************
export const getDoctorByEmailSafe = async (email) => {
  try {
    // Validate email format first
    if (!validateEmail(email)) {
      throw new Error('Invalid email format');
    }

    const response = await getDoctorByEmail(email);
    return {
      success: true,
      data: response.data,
      found: true
    };
  } catch (error) {
    // If it's a "not found" error, return a safe response
    if (error.includes('not found')) {
      return {
        success: true,
        data: null,
        found: false,
        message: 'Doctor not found with this email'
      };
    }
    
    // For other errors, throw them
    throw error;
  }
};

// ******************* Example usage functions *******************

// Example: Login by email
export const loginDoctorByEmail = async (email, password) => {
  try {
    // First get the doctor to verify they exist
    const doctorResponse = await getDoctorByEmailSafe(email);
    
    if (!doctorResponse.found) {
      throw new Error('Doctor not found with this email');
    }

    // Then proceed with login logic
    const loginResponse = await API.post('/api/auth/login', {
      email,
      password,
      role: 'doctor'
    });

    return loginResponse.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || error?.message || "Login failed"
    );
  }
};

// Example: Reset password by email
export const resetDoctorPassword = async (email) => {
  try {
    // First verify doctor exists
    const doctorResponse = await getDoctorByEmailSafe(email);
    
    if (!doctorResponse.found) {
      throw new Error('Doctor not found with this email');
    }

    // Send reset password request
    const response = await API.post('/api/auth/reset-password', {
      email,
      role: 'doctor'
    });

    return response.data;
  } catch (error) {
    throw (
      error?.response?.data?.message || error?.message || "Password reset failed"
    );
  }
};